import { Tuple2 }	from "ts-base/tuple";
import { Fn, Fn2 }	from "ts-base/fn";
import { Endo }		from "ts-base/endo";
import { Is }		from "ts-base/is";
import { Monoid }	from "ts-base/monoid";
import { Maybe }	from "ts-base/maybe";

export namespace Maps {
	//-----------------------------------------------------------------------------
	//## type helpers

	/** an identity function, but takes mutability away */
	export const readonly	= <K, V>(self:Map<K, V>):ReadonlyMap<K, V>	=> self;

	//-----------------------------------------------------------------------------
	//## construction

	export const empty	= <K, V>():Map<K, V>	=> new Map();

	export const single	= <K, V>(key:K, value:V):Map<K, V>	=>
		new Map([ [ key, value ] ]);

	export const fromPairs	= <K, V>(pairs:ReadonlyArray<Tuple2<K, V>>):Map<K, V>	=>
		new Map(pairs);

	export const toPairs	= <K, V>(self:ReadonlyMap<K, V>):Array<Tuple2<K, V>>	=>
		Array.from(self.entries());

	export const clone	= <K, V>(self:ReadonlyMap<K, V>):Map<K, V>	=>
		new Map(self);

	//-----------------------------------------------------------------------------
	//## conversion

	export const keySet		= <K>(self:ReadonlyMap<K, unknown>):Set<K>	=>
		new Set(self.keys());

	export const keyArray	= <K>(self:ReadonlyMap<K, unknown>):Array<K>	=>
		[ ...self.keys() ];

	export const valueSet	= <V>(self:ReadonlyMap<unknown, V>):Set<V>	=>
		new Set(self.values());

	export const valueArray	= <V>(self:ReadonlyMap<unknown, V>):Array<V>	=>
		[ ...self.values() ];

	//-----------------------------------------------------------------------------
	//## extraction

	export const has	= <K, V>(self:ReadonlyMap<K, V>) => (key:K):boolean	=>
		self.has(key);

	export const get	= <K, V>(self:ReadonlyMap<K, V>) => (key:K):Maybe<V>	=>
		self.has(key) ? Maybe.some(self.get(key) as V) : Maybe.none();

	export const getOr	= <K, V>(self:ReadonlyMap<K, V>) => (key:K) => <U>(alternative:U):V|U	=>
		self.has(key) ? self.get(key) as V : alternative;

	export const getOrNull	= <K, V>(self:ReadonlyMap<K, V>) => (key:K):V|null	=>
		self.has(key) ? self.get(key) as V : null;

	export const unsafeGet	= <K, V>(self:ReadonlyMap<K, V>) => (key:K):V => {
		if (!self.has(key))	throw new Error(`key ${String(key)} not found`);
		return self.get(key) as V;
	};

	//-----------------------------------------------------------------------------
	//## entry mapping

	export const throughPairs	= <K, V>(self:ReadonlyMap<K, V>) => <KK, VV>(func:Fn<ReadonlyArray<Tuple2<K, V>>, ReadonlyArray<Tuple2<KK, VV>>>):Map<KK, VV>	=>
		fromPairs(func(toPairs(self)));

	export const mapEntries	= <K, V>(self:ReadonlyMap<K, V>) => <KK, VV>(func:Fn2<K, V, Tuple2<KK, VV>>):Map<KK, VV>	=> {
		const out	= new Map();
		for (const [ k, v ] of self.entries()) {
			const [ kk, vv ]	= func(k, v);
			out.set(kk, vv);
		}
		return out;
	};

	//-----------------------------------------------------------------------------
	//## value mapping

	export const mapValues	= <K, V>(self:ReadonlyMap<K, V>) => <U>(func:Fn<V, U>):Map<K, U> => {
		const out	= new Map<K, U>();
		for (const [ k, v ] of self) {
			out.set(k, func(v));
		}
		return out;
	};

	export const mapValuesWithKey	= <K, V>(self:ReadonlyMap<K, V>) => <U>(func:Fn2<K, V, U>):Map<K, U> => {
		const out	= new Map<K, U>();
		for (const [ k, v ] of self) {
			out.set(k, func(k, v));
		}
		return out;
	};

	//-----------------------------------------------------------------------------
	//## value collapse

	export const collapseMapValues	= <K, V>(self:ReadonlyMap<K, V>) => <U>(func:Fn<V, Maybe<U>>):Map<K, U>	=> {
		const out	= new Map<K, U>();
		for (const [ k, v ] of self) {
			const maybe	= func(v);
			if (Maybe.isSome(maybe)) {
				out.set(k, maybe.value);
			}
		}
		return out;
	};

	//-----------------------------------------------------------------------------
	//## key filtering

	export const filterKeys	= <K, V>(self:ReadonlyMap<K, V>)	=> (pred:Fn<K, boolean>):Map<K, V>	=> {
		const out	= new Map<K, V>();
		for (const [ k, v ] of self) {
			if (pred(k))	out.set(k, v);
		}
		return out;
	};

	export const filterKeysIs	= <K, V>(self:ReadonlyMap<K, V>)	=> <L extends K>(pred:Is<K, L>):Map<L, V>	=> {
		const out	= new Map<L, V>();
		for (const [ k, v ] of self) {
			if (pred(k))	out.set(k, v);
		}
		return out;
	};

	//-----------------------------------------------------------------------------
	//## value filtering

	export const filterValues	= <K, V>(self:ReadonlyMap<K, V>) => (pred:Fn<V, boolean>):Map<K, V>	=> {
		const out	= new Map<K, V>();
		for (const [ k, v ] of self) {
			if (pred(v))	out.set(k, v);
		}
		return out;
	};

	export const filterValuesWithKey	= <K, V>(self:ReadonlyMap<K, V>) => (pred:Fn2<K, V, boolean>):Map<K, V>	=> {
		const out	= new Map<K, V>();
		for (const [ k, v ] of self) {
			if (pred(k, v))	out.set(k, v);
		}
		return out;
	};

	export const filterValuesIs	= <K, V>(self:ReadonlyMap<K, V>) => <U extends V>(pred:Is<V, U>):Map<K, U>	=> {
		const out	= new Map<K, U>();
		for (const [ k, v ] of self) {
			if (pred(v))	out.set(k, v);
		}
		return out;
	};

	//-----------------------------------------------------------------------------
	//## modification

	export const set	= <K, V>(self:ReadonlyMap<K, V>) => (key:K) => (value:V):Map<K, V>	=> {
		const result	= clone(self);
		result.set(key, value);
		return result;
	};

	export const modify	= <K, V>(self:ReadonlyMap<K, V>) => (key:K) => (func:Endo<V>):Map<K, V>	=> {
		const result	= clone(self);
		if (result.has(key)) {
			result.set(key, func(result.get(key) as V));
		}
		return result;
	};

	export const remove	= <K, V>(self:ReadonlyMap<K, V>) => (key:K):Map<K, V>	=> {
		const c	= clone(self);
		c.delete(key);
		return c;
	};

	export const setMember	= <K, V>(self:ReadonlyMap<K, V>) => (key:K) => (value:Maybe<V>):Map<K, V> =>
		Maybe.isSome(value) ? set(self)(key)(value.value) : remove(self)(key);

	export const modifyMember	= <K, V>(self:ReadonlyMap<K, V>) => (key:K) => (func:Endo<Maybe<V>>):Map<K, V>	=>
		setMember(self)(key)(func(get(self)(key)));

	//-----------------------------------------------------------------------------
	//## math

	export const prepend	= <K, V>(self:ReadonlyMap<K, V>) => (key:K, value:V):Map<K, V>	=>
		concat(single(key, value), self);

	export const append		= <K, V>(self:ReadonlyMap<K, V>) => (key:K, value:V):Map<K, V>	=>
		concat(self, single(key, value));

	export const concat		= <K, V>(as:ReadonlyMap<K, V>, bs:ReadonlyMap<K, V>):Map<K, V>	=>
		new Map([ ...as, ...bs ]);

	//-----------------------------------------------------------------------------
	//## typeclasses

	// TODO add Equal instance

	export const concatenatedMut	= <K, V>():Monoid<Map<K, V>>	=> ({
		empty:	empty(),
		concat:	concat,
	});

	export const concatenatedRo	= <K, V>():Monoid<ReadonlyMap<K, V>>	=> ({
		empty:	empty(),
		concat:	concat,
	});
}
